import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
/* import MetaHead from "./MetaHead"; */
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../store/hooks";
import { FormInput, FormButton } from "./FormComponents";
import { API_ENDPOINTS, authenticatedFetch } from "../config/api";
import {
  storeAuthData,
  LoginResponse,
  isAuthenticatedAndValid,
  getAppType,
} from "../utils/auth";
import { setUserProfile, updateStreakSpark } from "../store/slices/userSlice";
import { toUUID } from "../utils/uuidUtils";
import { fetchTodos } from "../store/slices/todosSlice";
import { getHabitsWithCompletions } from "../store/slices/habitsSlice";
import { trackPageVisit, trackLoginEvent } from "../utils/analytics";
/* import { fetchNotes } from '../store/slices/notesSlice';
import { fetchReminders } from '../store/slices/remindersSlice';
import { fetchTrackers, fetchTrackerEntries } from '../store/slices/trackersSlice'; */

interface LoginFormData {
  username: string;
  password: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState<LoginFormData>({
    username: "",
    password: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticatedAndValid()) {
      navigate("/app");
    }
  }, [navigate]);

  useEffect(() => {
    trackPageVisit("login");
  }, []);

  const handleInputChange = (field: keyof LoginFormData) => (value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleResendVerification = async () => {
    if (!formData.username) {
      setResendMessage("Please enter your email or username first");
      return;
    }

    setResendLoading(true);
    setResendMessage(null);

    try {
      const response = await fetch(API_ENDPOINTS.AUTH.RESEND_VERIFICATION, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: formData.username.includes("@") ? formData.username : null,
          username: !formData.username.includes("@") ? formData.username : null,
        }),
      });

      if (response.ok) {
        setResendMessage(
          "Verification email sent successfully. Please check your email.",
        );
      } else {
        const errorData = await response.json();
        setResendMessage(
          errorData.message || "Failed to send verification email",
        );
      }
    } catch (error) {
      setResendMessage("Network error. Please try again.");
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setShowResendVerification(false);
    setResendMessage(null);

    try {
      // Determine app type for session validity
      const appType = getAppType();

      // TODO: Replace with actual API call to /auth/login
      const response = await authenticatedFetch(API_ENDPOINTS.AUTH.LOGIN, {
        method: "POST",
        body: JSON.stringify({ ...formData, app: appType }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (
          errorData.message?.includes("email not verified") ||
          errorData.message?.includes("verify")
        ) {
          setShowResendVerification(true);
          throw new Error(
            "Please verify your email before logging in. Check your email for a verification link.",
          );
        }
        throw new Error("Login failed");
      }

      const data: LoginResponse = await response.json();

      // Store authentication data
      storeAuthData(data);

      // Update Redux store with user profile
      dispatch(
        setUserProfile({
          id: toUUID(data.user.id),
          username: data.user.username,
          email: data.user.email,
          name: data.user.name,
          password: "", // Don't store password in Redux
          createdAt: new Date().toISOString(),
          sparkPoints: data.user.sparkPoints || 0, // Default to 0 if not provided
          preferences: {
            dailyReminderNotificationTime: "20:00", // Default value
          },
          encryptionKey: "", // Default to empty string
          isEmailVerified: data.user.isEmailVerified || false, // Add email verification status
        }),
      );

      // Update streak spark data with total spark points
      dispatch(
        updateStreakSpark({
          totalSparkPoints: data.user.sparkPoints || 0,
        }),
      );

      // Calculate date range for habits with completions (3 months ago to 1 month ahead)
      const now = new Date();
      const startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1)
        .toISOString()
        .split("T")[0];
      const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 31)
        .toISOString()
        .split("T")[0];

      // Fetch all user data from API (fire-and-forget)
      dispatch(fetchTodos());
      dispatch(getHabitsWithCompletions({ startDate, endDate }));
      /* dispatch(fetchNotes());
      dispatch(fetchReminders());
      dispatch(fetchTrackers());
      dispatch(fetchTrackerEntries()); */

      // Track successful login event
      trackLoginEvent(data.user.username);

      // Determine redirect path
      const returnUrl = searchParams.get("returnUrl");
      let redirectPath = "/app";
      if (returnUrl) {
        try {
          const decodedUrl = decodeURIComponent(returnUrl);
          // Basic validation: ensure it's an internal path
          if (
            decodedUrl.startsWith("/") &&
            !decodedUrl.includes("://") &&
            decodedUrl !== "/login" &&
            decodedUrl !== "/register"
          ) {
            redirectPath = decodedUrl;
          }
        } catch (e) {
          // Invalid URL, use default
        }
      }

      // Navigate to the determined path
      navigate(redirectPath);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/*<MetaHead
        title={t('login.title')}
        description={t('login.description') as string}
        keywords={t('login.keywords') as string}
      />*/}
      <div className="min-h-screen flex items-center justify-center px-4 py-8 animate-fade-in">
        <div className="max-w-md w-full space-y-8">
          {/* Logo and Title */}
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl shadow-glow mb-8">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>

            <h2
              className="text-3xl font-bold bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent mb-2"
              style={{ whiteSpace: "pre-line" }}
            >
              {t("login.welcomeBack")}
            </h2>
            <p className="text-neutral-600 dark:text-neutral-400">
              {t("login.subtitle")}
            </p>
          </div>

          {/* Login Form */}
          <div className="glass-card p-8 rounded-2xl shadow-large">
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="space-y-5">
                <FormInput
                  id="username"
                  label={t("login.usernameOrEmail")}
                  type="text"
                  value={formData.username}
                  onChange={handleInputChange("username")}
                  placeholder={t("login.usernamePlaceholder") as string}
                  required
                />

                <FormInput
                  id="password"
                  label={t("login.password")}
                  type="password"
                  value={formData.password}
                  onChange={handleInputChange("password")}
                  placeholder={t("login.passwordPlaceholder") as string}
                  required
                />
              </div>

              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 text-red-500 mr-3 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <p className="text-red-700 dark:text-red-300 text-sm font-medium">
                      {error}
                    </p>
                  </div>
                  {showResendVerification && (
                    <div className="mt-4 pt-4 border-t border-red-200 dark:border-red-700">
                      <button
                        onClick={handleResendVerification}
                        disabled={resendLoading}
                        className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 text-sm"
                      >
                        {resendLoading
                          ? "Sending..."
                          : "Resend Verification Email"}
                      </button>
                      {resendMessage && (
                        <p
                          className={`mt-2 text-sm ${resendMessage.includes("successfully") ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}
                        >
                          {resendMessage}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-center">
                <FormButton
                  type="submit"
                  variant="primary"
                  loading={loading}
                  disabled={loading}
                  size="lg"
                >
                  {loading ? t("login.signingIn") : t("login.signIn")}
                </FormButton>
              </div>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                {t("login.noAccount")}{" "}
                <Link
                  to="/register"
                  className="font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
                >
                  {t("login.createAccount")}
                </Link>
              </p>
            </div>
          </div>

          {/* Features Preview */}
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="glass-card p-4 rounded-xl">
              <div className="w-8 h-8 bg-gradient-to-br from-secondary-500 to-secondary-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
                {t("login.trackHabits")}
              </div>
            </div>

            <div className="glass-card p-4 rounded-xl">
              <div className="w-8 h-8 bg-gradient-to-br from-accent-500 to-accent-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
                {t("login.calendarView")}
              </div>
            </div>

            <div className="glass-card p-4 rounded-xl">
              <div className="w-8 h-8 bg-gradient-to-br from-warning-500 to-warning-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                <svg
                  className="w-4 h-4 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
              </div>
              <div className="text-xs font-medium text-neutral-700 dark:text-neutral-300">
                {t("login.analytics")}
              </div>
            </div>
          </div>

          {/* Home Link */}
          <div className="text-center mt-8">
            <Link
              to="/"
              className="text-sm text-neutral-600 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            >
              {t("login.backToHome")}
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
